/**
 * If Firebase still has a session that requires email MFA, return the challenge
 * email so Login can restore the MFA sheet (reload / auth restore while gated).
 */
export async function resolvePendingEmailMfaChallenge(): Promise<string | null> {
  const { usesFirebaseAuth } = await import('./FirebaseAuthService');
  if (!usesFirebaseAuth()) return null;

  const { loadAuth } = await import('./firebaseAuthSession');
  const { auth } = await loadAuth();
  const fbUser = auth.currentUser;
  if (!fbUser) return null;

  const {
    isEmailMfaVerifiedFor,
    isEmailMfaGatePendingFor,
    markEmailMfaGatePending,
    clearEmailMfaGatePending,
  } = await import('./emailMfaSession');
  if (isEmailMfaVerifiedFor(fbUser.uid)) return null;

  // God never uses mailbox email MFA.
  try {
    const token = await fbUser.getIdTokenResult();
    if (String(token.claims?.role ?? '') === 'god') {
      clearEmailMfaGatePending();
      return null;
    }
  } catch {
    // Fall through to status check.
  }

  const { getEmailMfaStatus } = await import('./EmailMfaService');
  const status = await getEmailMfaStatus();
  if (!status.enrolled) {
    // Stale gate after enrollment was cleared must not keep the MFA sheet open.
    if (isEmailMfaGatePendingFor(fbUser.uid)) clearEmailMfaGatePending();
    return null;
  }

  markEmailMfaGatePending(fbUser.uid);
  const email = String(fbUser.email ?? '').trim();
  return email || null;
}
